import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from 'store/hook';
import { getMachinePromises } from 'handlers/util/fetch.all';

import TagHandler from 'handlers/tag.handler';
import EngineHoursHandler from 'handlers/engine.hours.handler';
import FuelPriceHandler from 'handlers/fuelPrice.handler';
import FleetOverviewHandler from 'handlers/fleetOverView.handler';
import TargetHandler from 'handlers/targets.handler';
import UtilizationHandler from 'handlers/utilization.handler';
import ProductionHandler from 'handlers/production.handler';

export const useMachinesInit = () => {
  const [loading, setLoading] = useState(false); // start false because it only loads if module is selected
  const location = useLocation();
  const customer = useAppSelector((state) => state.authReducer.customer);
  const customerId = customer.id;
  const isMachinesModule = customer.modules?.isMachinesModule ?? false;
  const isAuthenticated = useAppSelector(
    (state) => state.authReducer.isAuthenticated
  );

  const fetchedCustomerIdRef = useRef<string | null>(null);

  // Reset when customer changes
  useEffect(() => {
    if (
      fetchedCustomerIdRef.current &&
      fetchedCustomerIdRef.current !== customerId
    ) {
      fetchedCustomerIdRef.current = null;
    }
  }, [customerId]);

  useEffect(() => {
    const MACHINE_PATHS = [
      '/fleet',
      '/fleet-planner',
      '/engine-hours-summary',
      '/diagnostic-trouble-codes',
      '/sensor-analysis',
      '/ml-insights',
      '/road-condition-summary',
      '/driver-behavior',
      '/network-health',
      '/fuel-usage',
      '/utilization',
      '/production',
      '/frame-fatigue',
      '/settings/tags',
    ];

    const isMachineRoute = MACHINE_PATHS.some((path) =>
      location.pathname.startsWith(path)
    );

    if (
      !isAuthenticated ||
      !customerId ||
      !isMachinesModule ||
      !isMachineRoute
    ) {
      setLoading(false);
      return;
    }

    if (fetchedCustomerIdRef.current === customerId) {
      setLoading(false);
      return;
    }

    const fetchMachineData = async () => {
      fetchedCustomerIdRef.current = customerId;
      setLoading(true);

      try {
        const handlers = {
          tagHandler: new TagHandler(),
          engineHoursHandler: new EngineHoursHandler(),
          fuelPriceHandler: new FuelPriceHandler(),
          fleetOverviewHandler: new FleetOverviewHandler(),
          targetHandler: new TargetHandler(),
          utilizationHandler: new UtilizationHandler(),
          productionHandler: new ProductionHandler(),
        };

        // Helper function to calculate date ranges (copied from fetch.all.ts)
        const currentDate = new Date();
        const formattedDate = `${currentDate.getFullYear()}-${String(
          currentDate.getMonth() + 1
        ).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;

        const promises = getMachinePromises(
          handlers as any,
          formattedDate,
          customerId
        );

        await Promise.allSettled(promises);
      } catch (error) {
        console.error('Error fetching machine data:', error);
      } finally {
        setLoading(false);
      }
    };

    void fetchMachineData();
  }, [location.pathname, customerId, isAuthenticated, isMachinesModule]);

  return { loading };
};
